import React, { Component } from 'react'

import classNames from 'classnames'

import { Button } from '../Button/Button'
import styles from './Paginator.module.scss'

interface IProps {
  totalCount: number
  initialPage: number
  showCount: number
  withBtn?: boolean
  onChange: (currentPage: number, showCount: number, more?: boolean) => void
  onMoreBtnClick?: () => void
}

interface IState {
  showCount: number
  currentPage: number
}

export class Paginator extends Component<IProps, IState> {
  private limit = 8
  private preservedDistanceToEdge = 4

  constructor(props: IProps) {
    super(props)

    this.state = {
      showCount: props.showCount,
      currentPage: props.initialPage,
    }
  }

  componentDidUpdate(prev: IProps): void {
    if (
      prev.initialPage !== this.props.initialPage &&
      this.props.initialPage !== this.state.currentPage
    ) {
      this.setState({
        currentPage: this.props.initialPage,
      })
    }
    if (this.props.showCount !== prev.showCount) {
      this.setState({
        showCount: this.props.showCount,
      })
    }
  }

  handleOffsetChange = (selected: number): void => {
    this.setState(
      {
        showCount: selected,
        currentPage: 1,
      },
      () => {
        this.props.onChange(1, selected)
      }
    )
  }

  renderPage = (page: number, href: string): JSX.Element => {
    const cn = classNames([
      styles.page,
      { [styles.active]: page === this.state.currentPage },
    ])

    return (
      <div className={cn} key={page}>
        <a href={href} data-page={page} className={styles.pageLink}>
          {page}
        </a>
      </div>
    )
  }

  renderNavButton = (
    href: string,
    buttonType: 'prev' | 'next',
    isDisabled: boolean,
    goTo: number
  ): JSX.Element => {
    const isPrev = buttonType === 'prev'

    const cn = classNames([
      {
        [styles.pagePrev]: isPrev,
        [styles.pageNext]: !isPrev,
        [styles.disabled]: isDisabled,
      },
    ])
    const linkClasses = isPrev
      ? `${styles.pagePrevLink} mts-icon-nav-arrow-left`
      : `${styles.pageNextLink} mts-icon-nav-arrow-right`

    return (
      <div className={cn}>
        <a className={linkClasses} href={href} data-page={goTo}>
          {''}
        </a>
      </div>
    )
  }

  handleLink = (page: number): string => `?page=${page}`

  handlePage = (pageIndex: number): JSX.Element => {
    const { currentPage, showCount } = this.state
    const totalPage = Math.ceil(this.props.totalCount / showCount)
    const page = pageIndex + 1
    const distanceToLastPage = Math.abs(totalPage - page)
    const distanceToCurrent = Math.abs(page - currentPage)
    const isEdgePage = page === totalPage || page === 1
    const isLastPreservedRange =
      totalPage - currentPage < this.preservedDistanceToEdge &&
      distanceToLastPage < this.preservedDistanceToEdge
    let isFirstPreservedRange =
      page <= this.preservedDistanceToEdge + 1 &&
      currentPage <= this.preservedDistanceToEdge + 1
    const href = this.handleLink(page)

    if (
      currentPage === this.preservedDistanceToEdge + 1 &&
      totalPage > this.limit
    ) {
      isFirstPreservedRange = false
    }

    /* truncated */
    if (
      totalPage >= this.limit &&
      currentPage !== page &&
      !isEdgePage &&
      !isFirstPreservedRange &&
      !isLastPreservedRange &&
      distanceToCurrent > 1
    ) {
      return (
        <div className={styles.break} key={`paginator_truncate_${pageIndex}`}>
          ...
        </div>
      )
    }

    return this.renderPage(page, href)
  }

  handleClick = (ev: React.MouseEvent, more: boolean, page?: number): void => {
    ev.preventDefault()

    if (this.props?.onMoreBtnClick) {
      this.props.onMoreBtnClick()
    }

    const target = ev.target as HTMLElement
    const _page = page || parseInt(target.getAttribute('data-page') || '0', 10)

    if (_page && _page !== this.state.currentPage) {
      this.setState(
        {
          currentPage: _page,
        },
        () => {
          this.props.onChange(_page, this.state.showCount, more)
        }
      )
    }
  }

  render(): JSX.Element {
    const { currentPage, showCount } = this.state
    const count = this.props.totalCount

    const totalPage = showCount === 0 ? 0 : Math.ceil(count / showCount)

    const pageArray = isNaN(totalPage)
      ? []
      : Array.from(Array(totalPage).keys())

    const prev = currentPage - 1
    const next = currentPage + 1
    const isShowPrev = this.limit <= totalPage && currentPage !== 1
    const isShowNext = this.limit <= totalPage && currentPage !== totalPage
    const isShow = totalPage > 1
    return (
      <>
        {isShow && this.props.withBtn && (
          <Button
            className="mb-1"
            onClick={e => this.handleClick(e, true, currentPage + 1)}
            disabled={next > totalPage}
            btnType="secondary_light"
            full
          >
            Показать еще
          </Button>
        )}
        <div className={styles.wrapper}>
          {isShow && (
            <div
              className={styles.pages}
              onClick={e => this.handleClick(e, false)}
            >
              {isShowPrev &&
                this.renderNavButton(
                  this.handleLink(prev),
                  'prev',
                  currentPage === 1,
                  prev
                )}
              {pageArray.map(page => this.handlePage(page))}
              {isShowNext &&
                this.renderNavButton(
                  this.handleLink(next),
                  'next',
                  currentPage === totalPage,
                  next
                )}
            </div>
          )}
        </div>
      </>
    )
  }
}
